/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import Sticky from 'react-sticky-el';
import { motion } from 'framer-motion';
import { graphql } from 'gatsby';
import { rgba } from 'polished';
import styled, { css, ThemeProvider } from 'styled-components';

import { SEO } from '@/components/global/SEO';
import Page from '@/components/v2/Page';
import { V1PageWrapper } from '@/components/v2/Page/V1PageWrapper';

import {
  Button,
  GradientHeader,
  Heading,
  RunningText,
  Text,
  Voronoi
} from '../components/design-system';
import { type CustomerQuoteType } from '../components/global/CustomerQuote';
import { HeroTout } from '../components/global/HeroTout';
import QuotesMultiple from '../components/global/QuotesMultiple';
import { ReadyToGetStartedTout } from '../components/global/ReadyToGetStartedTout';
import { RichText } from '../components/global/RichText';
import { SocialShare } from '../components/global/SocialShare';
import { Tag } from '../components/global/Tag';
import { TronDivider } from '../components/global/TronDivider';
import { AuthorDescription } from '../components/use-cases/AuthorDescription';
import { LiveDemo } from '../components/use-cases/LiveDemo';
import {
  Recommendations,
  type RecommendationTypes
} from '../components/use-cases/Recommendations';
import {
  ANIMATIONS,
  INTERNAL_LINKS,
  SIGNUP_LINK,
  STICKY,
  TRACKING_LABELS
} from '../utils';
import { SOCIAL_SHARE } from '../utils/social-share-images';
import { LIGHT_THEME } from '../utils/theme/themes';

const Wrapper = styled(motion.div)`
  width: 100%;
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 60px 0;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.WHITE};
  z-index: 2;
  position: relative;

  @media (min-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    flex-direction: row;
    padding: 60px 0 80px 0;
  }
`;

const Main = styled.div`
  max-width: 720px;
  display: flex;
  flex-direction: column;
  padding-left: 40px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    margin: 0 auto;
    padding: 0 40px;
  }

  section div:first-child {
    padding-bottom: 14px;
  }
`;

const Aside = styled.aside`
  margin: 20px auto;

  @media (min-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    margin: 0;
    padding: 0 40px 0;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    margin: 40px 40px 0;
    padding: 0;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    margin: 20px 20px 0;
    padding: 20px 20px 0;
  }
`;

const StyledSticky = styled(Sticky)`
  width: 350px;

  > div {
    margin: 0;
    transition: all 0.3s ease-in-out;
  }

  .isSticky {
    margin: 120px 0 250px 0;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    width: 100%;
  }
`;

const StyledHeading = styled(Heading)`
  margin-bottom: 45px;
  padding: 0;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    margin-bottom: 25px;
  }
`;

const StickyCard = styled.div`
  background: rgba(173, 142, 182, 0.1);
  flex: 1;
  max-width: 565px;
  padding: 30px;
  gap: 20px;
  cursor: default;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    width: 100%;
    padding: 40px;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    max-width: 100%;
  }
`;

const StickyHeading = styled(GradientHeader).attrs({
  $twoStop: true
})`
  font-size: 28px;
  margin-bottom: 13px;

  @media (min-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    text-align: left !important;
  }
`;

const StickyText = styled(Text)`
  color: ${({ theme }) => rgba(theme.colors.PURPLE, 1)};
  text-align: center;
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 20px;

  @media (min-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    text-align: left;
  }
`;

const StickyButton = styled(Button)`
  margin: 0 auto;
  @media (min-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    margin: auto;
  }
`;

const LiveDemoWrapper = styled.div`
  width: 100%;
`;

const SeeMore = styled.div`
  width: 100%;
  padding: 80px 40px;
  background: ${({ theme }) => theme.gradientFade};
  position: relative;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    padding: 60px 20px;
  }
`;

const CTATout = styled.div`
  width: 100%;
`;

const ActionWrapper = styled.div<{ $useCaseHero?: boolean }>`
  display: flex;
  gap: 50px;
  ${({ $useCaseHero }) =>
    $useCaseHero
      ? css`
          flex-direction: row;
          width: 100%;
          justify-content: space-between;
        `
      : css`
          flex-direction: column;
        `}
`;

const TagWrapper = styled.div`
  > p {
    color: ${({ theme }) => theme.colors.BLACK};
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 32px;
    margin-bottom: 8px;
  }

  > div {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    padding-bottom: 40px;
  }
`;

interface TagsType {
  slug?: string;
  title?: string;
}
export interface UseCaseTemplateType {
  author?: string;
  authorPhoto?: {
    title: string;
    url: string;
  };
  autoplayingVideo?: {
    url: string;
  };
  body?: {
    raw: string;
    references: {
      __typename: string;
      filter(arg0: (ref: unknown) => boolean): unknown;
    };
  };
  category: {
    slug: string;
    title: string;
  };
  customerQuotes: CustomerQuoteType[];
  description: {
    description: string;
  };
  duplicateWorkflow?: boolean;
  illustration?: boolean;
  logicLink: string;
  logo: { url: string };
  name: string;
  primaryTout:
    | 'notebooks'
    | 'collaboration'
    | 'integrations'
    | 'security'
    | 'data-apps';
  publishedAppLink: string;
  recommendations: RecommendationTypes[];
  seoMetaDescription: string;
  seoTitle: string;
  slug: string;
  socialSharePreviewImage: {
    title: string;
    url: string;
  };
  tagLinks: TagsType[];
  title: string;
  useCaseHero?: boolean;
  video?: {
    url: string;
  };
  videoLength?: number;
}

interface UseCaseTemplateProps {
  data: { contentfulUseCase: UseCaseTemplateType };
}

const UseCaseTemplate: React.ComponentType<UseCaseTemplateProps> = React.memo(
  ({ data }) => {
    const useCaseData: UseCaseTemplateType = data.contentfulUseCase;
    const liveDemoRef = useRef<HTMLDivElement>(null);

    const getCopyLink = useCallback(() => {
      if (useCaseData.logicLink) {
        const id = useCaseData.logicLink.split('/')[5];
        return `https://app.hex.tech/new-project?baseHexId=${id}&baseOrgId=hex-public&signup=true`;
      }
      return ''; // Return a default value or handle the case where logicLink is not defined
    }, [useCaseData.logicLink]);

    const actionButton = useMemo(
      () => (
        <ActionWrapper $useCaseHero={useCaseData.useCaseHero}>
          {useCaseData.duplicateWorkflow ? (
            <Button
              link={getCopyLink()}
              noreferrer={false}
              size="large"
              stylez="loud"
              text="Copy this template"
              trackingLabel={TRACKING_LABELS.CopyTemplate}
            />
          ) : (
            <Button
              link={`${SIGNUP_LINK}?source=usecase`}
              size="large"
              stylez="loud"
              text="Get started for free"
            />
          )}
          <SocialShare title={useCaseData.seoTitle} />
        </ActionWrapper>
      ),
      [
        useCaseData.useCaseHero,
        useCaseData.duplicateWorkflow,
        useCaseData.seoTitle,
        getCopyLink
      ]
    );

    return (
      <Page
        seo={
          <SEO
            description={useCaseData.seoMetaDescription}
            image={
              useCaseData.socialSharePreviewImage
                ? useCaseData.socialSharePreviewImage.url
                : SOCIAL_SHARE.UseCases
            }
            title={useCaseData.seoTitle}
            url={`${INTERNAL_LINKS.Templates}${useCaseData.category.slug}/${useCaseData.slug}/`}
          />
        }>
        <V1PageWrapper>
          <Voronoi />
          <Wrapper {...ANIMATIONS.SimpleFadeIn}>
            <Inner>
              <HeroTout
                autoplayingVideoPath={
                  useCaseData.autoplayingVideo &&
                  useCaseData.autoplayingVideo.url
                }
                banner={{
                  to: `${INTERNAL_LINKS.Templates}${useCaseData.category.slug}/`,
                  text: `${useCaseData.category.title}`
                }}
                customActions={actionButton}
                headerContent={useCaseData.name}
                headerSize="medium"
                infoSize="large"
                publishedAppLink={useCaseData.publishedAppLink}
                subHeader={<AuthorDescription useCase={useCaseData} />}
                useCaseHero={useCaseData.useCaseHero}
                video={useCaseData.video && useCaseData.video.url}
                videoLength={useCaseData.videoLength}
                videoSize="compact"
              />
              {useCaseData.body && (
                <ThemeProvider theme={LIGHT_THEME}>
                  <Container className={STICKY.Boundary}>
                    <Main>
                      <StyledHeading renderAs="h2">
                        How to build: {useCaseData.name}
                      </StyledHeading>
                      <RunningText>
                        <RichText body={useCaseData.body} />
                      </RunningText>
                    </Main>
                    <Aside>
                      <StyledSticky
                        bottomOffset={180}
                        boundaryElement={`.${STICKY.Boundary}`}
                        stickyClassName={STICKY.Element}
                        stickyStyle={{ zIndex: 1 }}>
                        {useCaseData.tagLinks && (
                          <TagWrapper>
                            <p>Related tags</p>
                            <div>
                              {useCaseData.tagLinks.map((tag, i) => (
                                <Tag
                                  key={i}
                                  slug={tag.slug}
                                  title={tag.title}
                                />
                              ))}
                            </div>
                          </TagWrapper>
                        )}
                        <StickyCard>
                          <StickyHeading renderAs="h2">
                            Get started with {useCaseData.name}
                          </StickyHeading>
                          <StickyText size="large">
                            Click below to copy this free template to your
                            workspace.
                          </StickyText>
                          <StickyButton
                            link={getCopyLink()}
                            size="large"
                            stylez="loud"
                            text="Copy this template"
                          />
                        </StickyCard>
                      </StyledSticky>
                    </Aside>
                  </Container>
                </ThemeProvider>
              )}
              {useCaseData.publishedAppLink && !useCaseData.useCaseHero && (
                <LiveDemoWrapper ref={liveDemoRef}>
                  <LiveDemo
                    logicLink={useCaseData.logicLink}
                    publishedAppLink={useCaseData.publishedAppLink}
                  />
                </LiveDemoWrapper>
              )}
              {useCaseData.recommendations && (
                <SeeMore>
                  <Recommendations
                    items={useCaseData.recommendations}
                    showTron={false}
                  />
                </SeeMore>
              )}
              {useCaseData.customerQuotes && (
                <QuotesMultiple customerQuotes={useCaseData.customerQuotes} />
              )}
              <CTATout>
                <TronDivider />
                <ReadyToGetStartedTout />
              </CTATout>
            </Inner>
          </Wrapper>
        </V1PageWrapper>
      </Page>
    );
  }
);

export const pageQuery = graphql`
  query ($slug: String!) {
    contentfulUseCase(slug: { eq: $slug }) {
      author
      authorPhoto {
        url
        title
      }
      category {
        title
        slug
      }
      description {
        description
      }
      seoTitle
      seoMetaDescription
      socialSharePreviewImage {
        title
        url
      }
      duplicateWorkflow
      useCaseHero
      publishedAppLink
      logicLink
      gridImage {
        title
        url
      }
      video {
        url
      }
      autoplayingVideo {
        url
      }
      recommendations {
        ... on ContentfulUseCaseCategory {
          __typename
          slug
          title
          gridImage {
            url
            title
          }
          socialSharePreviewImage {
            url
            title
          }
          seoMetaDescription
        }
        ... on ContentfulUseCase {
          __typename
          slug
          category {
            slug
          }
          name
          author
          seoMetaDescription
          gridImage {
            url
            title
          }
        }
        ... on ContentfulBlogPost {
          __typename
          slug
          date
          title
          author
          seoMetaDescription
          heroImage {
            url
            title
          }
        }
      }
      name
      customerQuotes {
        author
        customer {
          name
        }
        quote {
          quote
        }
      }
      slug
      videoLength
      body {
        raw
        references {
          ... on ContentfulAsset {
            ...BlockContentfulAsset
          }
          ... on ContentfulBlogLoomVideo {
            ...BlockContentfulBlogLoomVideo
          }
          ... on ContentfulBlogHexEmbed {
            ...BlockContentfulBlogHexEmbed
          }
          ... on ContentfulBlogQuote {
            ...BlockContentfulBlogQuote
          }
          ... on ContentfulBlogCallout {
            ...BlockContentfulBlogCallout
          }
        }
      }
      tagLinks {
        slug
        title
      }
    }
  }
`;

export default UseCaseTemplate;
